import { Button, Image, Modal, ModalBody, ModalContent, ModalOverlay, Text, VStack } from '@chakra-ui/react'
import WelcomeIllustration from 'assets/images/welcome-illustration.svg'
import {
  AUTH_MOBILE_BUTTON_GRADIENT,
  AUTH_MOBILE_BUTTON_GRADIENT_HOVER,
  authMutedColor,
  authTitleColor
} from 'components/Layouts/AuthLayout/constants'
import { DARK_SURFACE_GRADIENT } from 'constants/dark-tokens'

// Copy per onboarding entry point. An invitation is to the Organization — the
// user is only rostered onto a Team afterwards, and may not be on one yet — so
// joining is phrased as the Organization, while the create-org path starts with
// a first team. Players get the training-focused wording instead.
const COPY = {
  player: {
    intro: 'Your training journey starts here.',
    body: 'Set up your profile and start exploring training programs designed to support your performance!'
  },
  invitation: {
    intro: 'Your Rally journey starts here.',
    body: 'Complete your profile and join your Organization to get started.'
  },
  // Signing up from an invitation link already put the user in the Organization, so
  // this one welcomes them into it instead of asking them to join — see `invitation`,
  // which still applies to accounts holding an invite they have yet to accept.
  joined: {
    intro: "You're in — welcome to your Organization.",
    body: 'Complete your profile to get started with your team.'
  },
  organization: {
    intro: 'Your Rally journey starts here.',
    body: 'Complete your profile and set up your first team to get started.'
  }
} as const

export type TWelcomeVariant = keyof typeof COPY

/**
 * Onboarding entry points announce themselves with `?welcome=<variant>`; every
 * one of them now lands on the merged `/dashboard`, so the URL is the only thing
 * that can tell an athlete apart from a coach who just created an Organization.
 * Returns null when the param is absent or unknown, i.e. no modal.
 */
export const parseWelcomeVariant = (value: string | null): TWelcomeVariant | null =>
  value && value in COPY ? (value as TWelcomeVariant) : null

interface WelcomeJourneyModalProps {
  isOpen: boolean
  variant: TWelcomeVariant
  /** Names the Organization in the `joined` greeting. Falls back to generic copy. */
  orgName?: string
  onLater: () => void
}

const WelcomeJourneyModal = ({ isOpen, variant, orgName, onLater }: WelcomeJourneyModalProps) => {
  const title = 'Welcome to RallyPD!'
  const { intro, body } = COPY[variant]
  const greeting = variant === 'joined' && orgName ? `You're in — welcome to ${orgName}.` : intro
  return (
  <Modal isOpen={isOpen} isCentered onClose={onLater} closeOnOverlayClick={false}>
    <ModalOverlay bg="blackAlpha.400" backdropFilter="blur(4px)" />
    <ModalContent
      mx={4}
      borderRadius="16px"
      p={0}
      maxW="420px"
      bg={DARK_SURFACE_GRADIENT}
      border="1px solid rgba(255, 255, 255, 0.06)"
    >
      <ModalBody p="40px">
        <VStack spacing={5} align="center" textAlign="center">
          <Image src={WelcomeIllustration} h="140px" objectFit="contain" />
          <VStack spacing={2}>
            <Text
              fontSize="22px"
              fontWeight="700"
              color={authTitleColor}
              m={0}
            >
              {title}
            </Text>
            <Text
              fontSize="14px"
              color={authMutedColor}
              m={0}
              lineHeight="1.6"
            >
              {greeting}
              <br />
              {body}
            </Text>
          </VStack>
          <VStack spacing={3} w="100%">
            <Button
              bg={AUTH_MOBILE_BUTTON_GRADIENT}
              color="white"
              borderRadius="999px"
              h="48px"
              fontSize="16px"
              fontWeight="600"
              w="100%"
              border="none"
              _hover={{ bg: AUTH_MOBILE_BUTTON_GRADIENT_HOVER }}
              _focus={{ boxShadow: 'none' }}
              isDisabled
            >
              Setup now
            </Button>
            <Button
              variant="ghost"
              color={authMutedColor}
              fontSize="14px"
              fontWeight="500"
              h="40px"
              w="100%"
              border="none"
              onClick={onLater}
              bg="transparent"
              _hover={{ bg: 'transparent', color: 'white' }}
            >
              Do it later
            </Button>
          </VStack>
        </VStack>
      </ModalBody>
    </ModalContent>
  </Modal>
  )
}

export default WelcomeJourneyModal
